// Fonte única da fórmula de comissão da plataforma. Antes disso a conta estava replicada em ~7 pontos
// (tela de comissões, relatórios legados do frontend e relatórios do catálogo no zandu-api) com DUAS
// variações divergentes — a de vendas somava os custos e multiplicava a taxa por quantity de novo.
//
// REGRA (confirmada com o negócio em 26/07/2026):
//   comissão = fixo + percentual − custos − taxa
// Custos SEMPRE debitam, nunca somam. É o campo "Outros custos a descontar" do cadastro do serviço
// (Service.comissionCosts / comissionCostsAttendant); quando não há custo a descontar ele vale 0, então a
// subtração é sempre segura. A taxa (comissionFeeDebt) é a parcela da taxa do meio de pagamento que desconta
// da comissão, e só é diferente de zero quando a flag do meio de pagamento está ativa.
// O mesmo cálculo canônico está em 08-zandu-pos-api/src/modules/invoices/invoices-comissions.service.ts
// (computeServiceCommission), cujo comentário registra: "a subtração dos custos é a fórmula validada como correta".
//
// NÃO multiplique por quantity ao usar este helper — os dois modelos de origem já persistem os componentes
// no total do item:
//   - ServicesOnInvoices (pos-api): fixo/percentual/custos são gravados × quantity, e comissionFeeDebt já
//     tem o quantity embutido no próprio cálculo.
//   - AppointmentItem (scheduler-api): o item representa uma unidade, e comissionFeeDebt é por unidade.
// Por isso a mesma fórmula serve para venda e para atendimento, sem parâmetro de domínio.
//
// Este arquivo vive em src/modules porque é espelhado para 30-zandu-api/src/modules/api — assim frontend e
// relatórios do zandu-api compartilham a MESMA implementação. Ao alterar, replique no espelho.

// Aceita tanto AppointmentItem (scheduler) quanto ServicesOnInvoices (pos): só exige os componentes da conta,
// todos opcionais para tolerar payloads parciais (ex: relatórios que trazem só um subconjunto de campos).
export interface ComissionComponents {
    comissionFixed?: number
    comissionPercentage?: number // JÁ é o valor em R$ da parcela percentual — não é um percentual
    comissionCosts?: number
    comissionFeeDebt?: number
    comissionCanceled?: boolean
}

// Comissão de UM item, pela fórmula pura — IGNORA o cancelamento de propósito.
// Use quando o cancelamento é tratado à parte pelo chamador (ex: a tela de comissões exibe o valor cheio e
// apenas marca a linha como removida).
// Pode ser negativo de propósito: custo/taxa maior que a comissão normalmente indica erro de cadastro e
// precisa ficar explícito no valor, não truncado em zero.
export function calcComission(item: ComissionComponents): number {
    if (!item) return 0
    return (item.comissionFixed || 0) + (item.comissionPercentage || 0) - (item.comissionCosts || 0) - (item.comissionFeeDebt || 0)
}

// Comissão efetivamente devida: 0 quando a comissão do item foi cancelada.
// A EXCLUSÃO É OPT-IN POR RELATÓRIO, de propósito: trocar calcComission por esta função muda números já
// publicados, então cada consumidor decide explicitamente. Não existe filtro no lado da API e não deve
// existir — filtrar o item na origem removeria também a VENDA (valor, quantidade, taxas), e cancelar uma
// comissão não desfaz a venda. O correto é sempre trazer o item e zerar só a comissão, aqui.
// O flag chega em ambas as origens sem nenhum ajuste: ServicesOnInvoices (pos-api findAll usa
// include: { services }) e AppointmentItem (scheduler-api findAll usa include: { items: true }).
export function calcComissionPayable(item: ComissionComponents): number {
    if (!item || item.comissionCanceled) return 0
    return calcComission(item)
}

// Soma a comissão devida de uma lista de itens (venda ou atendimento), já descartando os cancelados.
export function sumComissionPayable(items: ComissionComponents[]): number {
    return sumComissionParts(items).total
}

// Parcelas de comissão de uma lista de itens, JÁ EXCLUINDO os cancelados — e o total que elas formam.
//
// Existe porque somar parcela a parcela na mão (um reduce por campo) foi exatamente onde a regra se perdeu:
// cada tela precisava lembrar de filtrar o cancelado em CADA reduce, e o painel "Comissão do atendimento"
// não lembrou — passou a exibir parcelas que não fechavam com o próprio total exibido ao lado.
//
// A garantia que este helper dá é a que faltava: total é derivado das MESMAS parcelas retornadas, então
// "(+) percentual (+) fixo (−) custos (−) taxa" sempre bate com o "(=) total" exibido. Não dá para uma
// parcela incluir cancelado e o total não (ou vice-versa) — é impossível por construção, não por disciplina.
//
// Use sempre que a tela exibir a COMPOSIÇÃO da comissão. Para só o valor, sumComissionPayable basta.
export interface ComissionParts {
    percentage: number
    fixed: number
    costs: number
    feeDebt: number
    total: number // fixed + percentage − costs − feeDebt
}

export function sumComissionParts(items: ComissionComponents[]): ComissionParts {
    const parts: ComissionParts = { percentage: 0, fixed: 0, costs: 0, feeDebt: 0, total: 0 }
    for (const item of items || []) {
        if (!item || item.comissionCanceled) continue
        parts.percentage += item.comissionPercentage || 0
        parts.fixed += item.comissionFixed || 0
        parts.costs += item.comissionCosts || 0
        parts.feeDebt += item.comissionFeeDebt || 0
    }
    parts.total = parts.fixed + parts.percentage - parts.costs - parts.feeDebt
    return parts
}
